import * as React from 'react';
import { FC, useState } from 'react';

import { useIntl } from 'react-intl';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { FavoriteIcon } from 'components/favorite-icon';
import Animation from 'components/lottie/favorite/loading';
import { SaveFavoriteOrder } from 'components/save-favorite-order';
import {
  GetUserFavoritesRefsDocument,
  ICartEntryInput,
  useCreateFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserFavoritesRefsQuery,
} from 'generated/rbi-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import { isOfferType } from 'state/order/utils';
import { frontendToBackendCartEntryMap } from 'utils/cart';
import { mapBackendToFrontend } from 'utils/reorder';
import { toast } from 'utils/toast';

import { IconButton } from './favorite-order-button.styled';
import { IFavoriteOrderButtonProps } from './types';

/**
 *
 * Favorite Order Button
 *
 */
const FavoriteOrderButton: FC<IFavoriteOrderButtonProps> = ({ order }) => {
  const { formatMessage } = useIntl();
  const { rbiOrderId } = order;
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);

  const shouldDisplayHeart = !(order.cart.cartEntries || []).some((entry: IBackendCartEntries) =>
    isOfferType(entry.type)
  );

  const { data } = useGetUserFavoritesRefsQuery({
    skip: !shouldDisplayHeart,
  });

  const [deleteFavorite, { loading: deleteLoading }] = useDeleteFavoriteMutation({
    onCompleted: () => toast.success(formatMessage({ id: 'removeFavoriteSucess' })),
  });
  const [createFavorite, { loading: createLoading }] = useCreateFavoriteMutation({
    onCompleted: () => toast.success(formatMessage({ id: 'saveFavoriteOrderSucess' })),
  });

  const onClick = () => {
    if (isFavorite) {
      openRemoveFavoriteModal();
    } else {
      setSaveModalOpen(true);
    }
  };

  const entries = (order.cart.cartEntries.map(cartEntry =>
    frontendToBackendCartEntryMap(mapBackendToFrontend(cartEntry))
  ) as unknown) as ICartEntryInput[];

  const saveFavoriteOrder = async (name: string) => {
    return await createFavorite({
      variables: {
        input: {
          entries,
          name,
          ref: rbiOrderId,
        },
      },
      refetchQueries: [{ query: GetUserFavoritesRefsDocument }],
    });
  };

  const removeFavoriteOrder = async (favoriteId: string) => {
    return await deleteFavorite({
      variables: {
        favoriteId,
      },
      refetchQueries: [{ query: GetUserFavoritesRefsDocument }],
    });
  };

  const refFavorite = data?.userFavorites.favorites?.find(favorite => favorite?.ref === rbiOrderId);
  const isFavorite = !!refFavorite;

  const [RemoveFavoriteModal, openRemoveFavoriteModal] = useDialogModal({
    onConfirm: () => removeFavoriteOrder(refFavorite?._id ?? ''),
    showCancel: true,
  });

  const loading = createLoading || deleteLoading;

  const FavIcon = <FavoriteIcon isFavorite={isFavorite} />;

  return shouldDisplayHeart ? (
    <>
      <IconButton onClick={onClick} data-testid="favorite-order-icon">
        {loading ? (
          <React.Suspense data-testid="fav_order_loading_animation" fallback={FavIcon}>
            <Animation />
          </React.Suspense>
        ) : (
          FavIcon
        )}
      </IconButton>

      <RemoveFavoriteModal
        buttonLabel={formatMessage({ id: 'remove' })}
        heading={formatMessage({ id: 'removeFavorite' })}
        body={formatMessage({ id: 'removeFavoriteConfirm' })}
        showCloseButton={true}
      />
      {saveModalOpen && (
        <SaveFavoriteOrder
          onSave={name => {
            saveFavoriteOrder(name);
            setSaveModalOpen(false);
          }}
          onDismiss={() => setSaveModalOpen(false)}
        />
      )}
    </>
  ) : null;
};

export default FavoriteOrderButton;
